import React from "react";
import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import type { CycleEntry } from "../../../store/profiling/types";
import { cycleBatches, batchKey, TRIGGER_COLORS, TRIGGER_LABELS, formatMs } from "./viz";
import { useContainerWidth } from "./useContainerWidth";

const LANE_HEIGHT = 26;
const LANE_GAP = 4;
const AXIS_HEIGHT = 18;

// Lane order top-to-bottom.
const LANES: ProfileData["trigger"][] = ["render", "callback", "react"];

type Props = {
    cycle: CycleEntry;
    selectedBatchKey: string | null;
    onSelectBatch: (batch: ProfileData) => void;
};

function ProfileTimeline({ cycle, selectedBatchKey, onSelectBatch }: Props) {
    // Horizontal zoom/pan: screenX = baseX * k + tx.
    const [k, setK] = React.useState(1);
    const [tx, setTx] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dragRef = React.useRef<{ startX: number; startTx: number } | null>(null);
    // Fills its pane and reflows on resize (pop-out-safe — see the hook).
    const width = useContainerWidth(containerRef, 600, 240);

    // Reset the view whenever the selected cycle changes.
    React.useEffect(() => { setK(1); setTx(0); }, [cycle.cycleId]);

    const batches = React.useMemo(() => cycleBatches(cycle), [cycle]);

    const { domainMin, domainSpan } = React.useMemo(() => {
        let min = Infinity, max = -Infinity;
        for (const b of batches) {
            min = Math.min(min, b.startTime);
            max = Math.max(max, b.startTime + b.totalTime);
        }
        if (!Number.isFinite(min)) { min = cycle.startTime; max = cycle.startTime + 1; }
        return { domainMin: min, domainSpan: Math.max(1e-6, max - min) };
    }, [batches, cycle.startTime]);

    const baseX = (t: number) => ((t - domainMin) / domainSpan) * width;
    const screenX = (t: number) => baseX(t) * k + tx;

    const onWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        const cursor = rect ? e.clientX - rect.left : width / 2;
        const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
        const newK = Math.max(1, Math.min(200, k * factor));
        // Keep the point under the cursor stationary while zooming.
        const newTx = cursor - (cursor - tx) * (newK / k);
        setK(newK);
        setTx(clampTx(newTx, newK));
    };

    const clampTx = (candidate: number, kk: number) => {
        const min = width - width * kk; // right edge cannot pull left of the viewport
        return Math.max(min, Math.min(0, candidate));
    };

    const onMouseDown = (e: React.MouseEvent) => { dragRef.current = { startX: e.clientX, startTx: tx }; };
    const onMouseMove = (e: React.MouseEvent) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        setTx(clampTx(dragRef.current.startTx + dx, k));
    };
    const endDrag = () => { dragRef.current = null; };

    const laneIndex = (trigger: ProfileData["trigger"]) => LANES.indexOf(trigger);
    const height = AXIS_HEIGHT + LANES.length * (LANE_HEIGHT + LANE_GAP);

    // A few axis ticks in ms relative to the cycle start.
    const ticks = React.useMemo(() => {
        const count = 5;
        return Array.from({ length: count + 1 }, (_, i) => domainMin + (domainSpan * i) / count);
    }, [domainMin, domainSpan]);

    return (
        <div className="profile-timeline">
            <div className="timeline-legend">
                {LANES.map(tr => (
                    <span key={tr} className="legend-item">
                        <span className="legend-swatch" style={{ background: TRIGGER_COLORS[tr] }} />
                        {TRIGGER_LABELS[tr]}
                    </span>
                ))}
                <span className="timeline-hint">scroll to zoom · drag to pan</span>
            </div>
            <div
                className="timeline-viewport"
                ref={containerRef}
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
            >
                <svg width={width} height={height} className="timeline-svg">
                    {/* axis ticks */}
                    {ticks.map((t, i) => {
                        const x = screenX(t);
                        if (x < -40 || x > width + 40) return null;
                        return (
                            <g key={i} transform={`translate(${x}, 0)`}>
                                <line y1={AXIS_HEIGHT} y2={height} className="timeline-gridline" />
                                <text x={2} y={12} fontSize={10} className="timeline-tick">{formatMs(t - domainMin)}</text>
                            </g>
                        );
                    })}
                    {/* batches */}
                    {batches.map(b => {
                        const x = screenX(b.startTime);
                        const w = Math.max(2, (baseX(b.startTime + b.totalTime) - baseX(b.startTime)) * k);
                        const y = AXIS_HEIGHT + laneIndex(b.trigger) * (LANE_HEIGHT + LANE_GAP);
                        const selected = batchKey(b) === selectedBatchKey;
                        return (
                            <g
                                key={batchKey(b)}
                                transform={`translate(${x}, ${y})`}
                                className="timeline-batch"
                                onClick={() => onSelectBatch(b)}
                            >
                                <title>
                                    {`${TRIGGER_LABELS[b.trigger]} · #${b.renderId}\n`}
                                    {`Template: ${b.fileName ?? "(inline)"}\n`}
                                    {`Total: ${formatMs(b.totalTime)}  ·  Start +${formatMs(b.startTime - domainMin)}\n`}
                                    {`Nodes: ${b.nodes.length}`}
                                </title>
                                <rect
                                    width={w}
                                    height={LANE_HEIGHT}
                                    rx={2}
                                    fill={TRIGGER_COLORS[b.trigger]}
                                    opacity={selected ? 1 : 0.82}
                                    stroke={selected ? "#0b0b0b" : "none"}
                                    strokeWidth={selected ? 1.5 : 0}
                                />
                                {w > 44 && (
                                    <text x={5} y={LANE_HEIGHT / 2} dominantBaseline="central" fontSize={10} fill="#ffffff">
                                        {formatMs(b.totalTime)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

export default React.memo(ProfileTimeline);
